// react 
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// antd 
import {
  CloudOutlined,
  LeftOutlined,
  RightOutlined,
  WarningFilled
} from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  Flex,
  message,
  Row,
  Select,
  Space,
  Tag,
  Tooltip
} from "antd";
// liberaries 
import {
  GoogleMap,
  Marker,
  MarkerClusterer
} from "@react-google-maps/api";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
// API's 
import {GetHomeLocations, GetHomeMapFilterProducts} from "../../api's/homeAPI";
import {GetShipmentAlerts,GetSingleShipment} from "../../api's/shipment";
// helper / components
import { appHelper, useToken } from "../../common/helper";
import { HometempratureList, RiskLevelList } from "../../common/static_data.js";
import {HomeMapMultiSelectDropdown, HomeEmptyShipments} from "./partials";
import {
  CustomModalWithoutButton,
  CustomParagraph,
  CustomTitle,
  StatusCardNotification,
  TeamBadge,
  WeatherLegend,
} from "../../components";
import {BatteryModal, HumidityModal, LightModal, TemperatureModal} from "../Modals";
// hooks 
import { useGoogleMaps } from "../../components/GoogleMapsProvider";
import { useSpin } from "../../components/SpinContext";
// redux 
import { fetchAllProducts } from "../../reudx-toolkit/slice/ProductSlice";
// images / icons 
import {AirportIcon, clusterImg, doubleArrow, Truck} from "../../assets/images";
import {
  BatteryIcon,
  CrossIcon,
  HomeProductIcon,
  HomeRiskIcon,
  HomeTeamsIcon,
  HomeTemperatureIcon,
  HumidityIcon,
  LightIcon,
  PauseIcon,
  PlayIcon,
  PressureIcon,
  TempratureRangeIcon,
  TiltIcon,
  VibilityWindowIcon
} from "../../assets/icons/icons.js";


const CustomForecastSlider = ({ value, max, onChange }) => {
  const sliderRef = useRef(null);
  const handleRef = useRef(null);

  const getPercentage = (currentValue) => (currentValue / max) * 100;

  const findClosestStep = (val) => {
    const step = 3; // 3-hour intervals
    const rawValue = (val / max) * 72;
    const snappedValue = Math.round(rawValue / step) * step;
    return snappedValue;
  };

  const handleInteraction = (e) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    let percent = x / rect.width;
    percent = Math.max(0, Math.min(1, percent));
    const rawValue = percent * max;
    const snappedValue = findClosestStep(rawValue);
    onChange(snappedValue);
  };

  const handleMouseDown = (e) => {
    handleInteraction(e);
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleInteraction);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleInteraction);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e) => {
    handleInteraction(e);
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleInteraction);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    document.addEventListener('touchmove', handleInteraction);
    document.addEventListener('touchend', handleTouchEnd);
  }

  const progress = getPercentage(value);

  return (
    <div 
      className="forecast-slider-wrapper" 
      ref={sliderRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="forecast-slider-track-background" />
      <div className="forecast-slider-track-progress" style={{ width: `${progress}%` }} />
      <div className="forecast-scrubber-container" style={{ left: `${progress}%` }}>
        <div className="scrubber-box">
          <span>{value === 0 ? 'Now' : `+${value} hrs`}</span>
        </div>
        <div className="scrubber-line"></div>
      </div>
      <div 
        className="forecast-slider-handle" 
        ref={handleRef}
        style={{ left: `${progress}%` }} 
      />
    </div>
  );
};

const HomeGoolgeMap = ({ isShipments, loading , allTeams, hasValidTeams , teamsPermissions }) => {
  const [mapShipmentData, setMapShipmentData] = useState([]);
  const [badgeValue, setBadgeValue] = useState(null);

  const truncateText = (text, maxLength = 23) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const main_menu = useSelector((state) => state.menu.items);
  const settings_sub_menu = main_menu?.find(
    (item) => item?.name === "Settings"
  );
  const productPermissions = settings_sub_menu?.sub_menus
    ? settings_sub_menu?.sub_menus?.find((item) => item?.name === "Products")
      ?.permissions?.is_view
    : false;

  const shipmentPermissions = main_menu?.find(
    (items) => items?.name.toLowerCase() == "shipment"
  );
  const allPermissions = shipmentPermissions?.permissions;
  // get all shipments locations
  const fetchHomeShipments = () => {
    showSpin();
    GetHomeLocations(token)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let resp = data?.data?.data ? data?.data?.data : [];
            if (resp) {
              setBadgeValue(resp?.in_transit_count);
            
              const filteredData = resp?.shipment_data?.filter(
                (location) =>
                  typeof location?.latest_location?.latest_lat === "number" &&
                  typeof location?.latest_location?.latest_long === "number" &&
                  location.latest_location.latest_lat !== 0 &&
                  location.latest_location.latest_long !== 0
              );
            
              setMapShipmentData(filteredData);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    fetchHomeShipments();
  }, []);

  const { isLoaded } = useGoogleMaps();
  const { changeToTimeString } = appHelper();
  const [alertIndex, setAlertIndex] = useState(0);
  const { showSpin, hideSpin } = useSpin();
  const { token, removeToken } = useToken();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const [productList, setProductList] = useState([]);
  const [lightModalOpen, setLightModalOpen] = useState(false);
  const [humidityModalOpen, setHumidityModalOpen] = useState(false);
  const [tempratureModalOpen, setTempratureModalOpen] = useState(false);
  const [batteryModalOpen, setBatteryModalOpen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    if (dispatch) {
      dispatch(fetchAllProducts(token));
    }
  }, [dispatch]);

  useEffect(() => {
    if (state) {
      if (state?.Products?.productList.length > 0) {
        setProductList([
          // { value: "", label: "All products" },
          ...(state?.Products?.productList?.map((product) => ({
            id: product.id,
            name: product.name,
          })) || []),
        ]);
      }
    }
  }, [state]);

  const [filterQuery, setFilterQuery] = useState(
    {
      teams: [],
      products: [],
      temprature: [],
      risk: []
    }
  );

  const handleProductApply = (ids) => {
    setFilterQuery((prev) => ({ ...prev, products: ids }));
  };
  const handleTeamsApply = (ids) => {
    setFilterQuery((prev) => ({ ...prev, teams: ids }));
  };
  const handleTempratureApply = (ids) => {
    setFilterQuery((prev) => ({ ...prev, temprature: ids }));
  };
  const handleRiskApply = (ids) => {
    setFilterQuery((prev) => ({ ...prev, risk: ids }));
  };

  const handleFilterProducts = (value) => {
    GetHomeMapFilterProducts(token, filterQuery)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let resp = data?.data?.data ? data?.data?.data : null;
            if (resp) {
              setBadgeValue(resp?.in_transit_count);
              const filteredData = resp?.shipment_data?.filter(
                (location) =>
                  typeof location?.latest_location?.latest_lat === "number" &&
                  typeof location?.latest_location?.latest_long === "number" &&
                  location.latest_location.latest_lat !== 0 &&
                  location.latest_location.latest_long !== 0
              );
              setMapShipmentData(filteredData);
            }
          } else {
            //    message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    handleFilterProducts();
  }, [filterQuery]);

  const [center, setCenter] = useState({ lat: 33.9422, lng: -118.4036 });

  const mapStyles = [
      {
      elementType: "geometry",

      stylers: [
        {
          color: "#ecf0f7",
        },
      ],
    },

    {
      featureType: "administrative.country",

      elementType: "geometry.stroke",

      stylers: [
        {
          color: "#818287",
        },
      ],
    },

    {
      featureType: "administrative.country",

      elementType: "labels.text.fill",

      stylers: [
        {
          color: "#000000",
        },
      ],
    },

    {
      featureType: "administrative.locality",

      elementType: "labels.text.fill",

      stylers: [
        {
          color: "#576173",
        },
      ],
    },

    {
      featureType: "administrative.locality",

      elementType: "labels.text.stroke",

      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },

    {
      featureType: "administrative.neighborhood",

      elementType: "labels.text.fill",

      stylers: [
        {
          color: "#85a0d5",
        },
      ],
    },

    {
      featureType: "administrative.neighborhood",

      elementType: "labels.text.stroke",

      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },

    {
      featureType: "administrative.province",

      elementType: "geometry.stroke",

      stylers: [
        {
          color: "#f5b2a6",
        },
      ],
    },

    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#a7e0b6",
        },
      ],
    },

    {
      featureType: "landscape.man_made",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#DEE2E8",
        },
      ],
    },

    {
      featureType: "road",
      elementType: "geometry",
      stylers: [
        {
          color: "#ffffff",
        },
      ],
    },

    {
      featureType: "road.highway",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#859ad0",
        },
      ],
    },

    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },

    {
      featureType: "transit.station.airport",
      elementType: "geometry.fill",
      stylers: [
        {
          color: "#cad4e0",
        },
      ],
    },

    {
      featureType: "water",
      stylers: [
        {
          color: "#c6dbff",
        },
      ],
    },
  ];

  const polylineOptions = {
    strokeColor: "#266EF1",
    strokeOpacity: 1.0,
    strokeWeight: 2,
    fillColor: "#266EF1",
    icons: [
      {
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 2,
          strokeColor: "#266EF1",
          fillColor: "#266EF1",
        },
        offset: "50%",
        repeat: "100px",
      },
    ],
  };

  const polylinePath = {
    strokeColor: "#4b4b4b",
    strokeOpacity: 1,
    strokeWeight: 3,
    fillColor: "#4b4b4b",
    icons: null,
  };

  var markerSize = {
    url: Truck,
    scaledSize: new window.google.maps.Size(40, 40),
  };

  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  const circleIcon = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: "#266EF1",
    fillOpacity: 1,
    strokeColor: "#266EF1",
    strokeWeight: 2,
    scale: 4,
  };

  const blackMarker = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: "#4B4B4B",
    fillOpacity: 1,
    strokeColor: "#4B4B4B",
    strokeWeight: 2,
    scale: 10,
  };

  const whiteMarker = {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: "#fff",
    fillOpacity: 1,
    strokeColor: "#4B4B4B",
    strokeWeight: 3,
    scale: 10,
  };

  const unauthorize = (error) => {
    if (error && error?.status === 401) {
      removeToken();
      navigate("/login");
    }
  };

  const goToDetail = () => {
    if (shipment && shipment?.id) {
      let model = { drawerState: "open", shipmentId: shipment?.id };
      navigate("/shipment", { state: model });
    }
  };

  const airMarker = {
    url: AirportIcon,
    scaledSize: new window.google.maps.Size(30, 30),
  };

  const [shipment, setShipment] = useState({});

  const [alertsData, setAlertsData] = useState(null);
  const [alertType, setAlertType] = useState(null);

  //   get single shipment alerts data on home map
  const getShipmentAlerts = (id) => {
    showSpin();
    GetShipmentAlerts(token, id)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let resp = data?.data?.data ? data?.data?.data : [];
            if (resp) {
              setAlertsData(resp);
              setAlertType(resp.alert_type);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const fetchSingleShipmentData = (id) => {
    showSpin();
    GetSingleShipment(token, id, true)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let resp = data?.data?.data ? data?.data?.data : null;
            if (resp) {
              setShipment(resp);
              setShowInfoModal(true);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const getCurrentPointInfo = (item) => {
    getShipmentAlerts(item?.shipment_id);
    fetchSingleShipmentData(item?.shipment_id);
  };

  const handleLightModal = () => {
    if (shipment?.shipment_monitor && shipment?.shipment_monitor?.length > 0) {
      setLightModalOpen(true);
    }
  };

  const handleHumidityModal = () => {
    if (shipment?.shipment_monitor && shipment?.shipment_monitor?.length > 0) {
      setHumidityModalOpen(true);
    }
  };

  const handleTempratureModal = () => {
    if (shipment?.shipment_monitor && shipment?.shipment_monitor?.length > 0) {
      setTempratureModalOpen(true);
    }
  };

  const handleBatteryModal = () => {
    if (shipment?.shipment_monitor && shipment?.shipment_monitor?.length > 0) {
      setBatteryModalOpen(true);
    }
  };

  const closeInfoModal = () => {
    setShipment({});
    setShowInfoModal(false);
  };

  const [map, setMap] = useState(null);
  const [showWeatherLayer, setShowWeatherLayer] = useState(false);
  const currentWeatherLayerRef = useRef(null);
  const preloadWeatherLayerRef = useRef(null);
  const [weatherLayerType, setWeatherLayerType] = useState("precipitation_new");
  const [forecastHourOffset, setForecastHourOffset] = useState(0);
  const [forecastStartTime, setForecastStartTime] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playIntervalRef = useRef(null);

  // Available weather layer types
  const weatherLayerOptions = {
    precipitation_new: "Precipitation",
    temp_new: "Temperature",
    wind_new: "Wind Speed",
  };

  const allForecastOptions = Array.from({ length: (72/3) + 1 }, (_, i) => ({
    value: i * 3,
    label: i === 0 ? "Current" : `+${i * 3}h`,
  }));

  const forecastMarksToShow = allForecastOptions.filter(opt => 
    [0, 3, 6, 12, 24, 36, 48, 72].includes(opt.value)
  );

  const forecastOptions = [
    { label: "Current", value: 0 },
    { label: "+3h", value: 3 },
    { label: "+6h", value: 6 },
    { label: "+12h", value: 12 },
    { label: "+24h", value: 24 },
    { label: "+48h", value: 48 },
    { label: "+72h", value: 72 },
  ];

  const v2LayerMap = {
    precipitation_new: 'PA0',
    temp_new: 'TA2',
    wind_new: 'WS10', // Changed from WND to WS10 to remove direction arrows
  };

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const clearWeatherLayers = useCallback(() => {
    if (!map) return;
  
    const removeLayer = (layerRef) => {
      if (layerRef.current) {
        // The Google Maps API doesn't provide a direct way to find a layer,
        // so we iterate and remove. A bit inefficient but necessary.
        for (let i = 0; i < map.overlayMapTypes.getLength(); i++) {
          if (map.overlayMapTypes.getAt(i) === layerRef.current) {
            map.overlayMapTypes.removeAt(i);
            break;
          }
        }
        layerRef.current = null;
      }
    };
  
    removeLayer(currentWeatherLayerRef);
    removeLayer(preloadWeatherLayerRef);
  }, [map]);

  const createWeatherLayer = useCallback((layerType, forecastOffset, opacity = 0.8) => {
    if (!map) return null;
    if (!process.env.REACT_APP_OPENWEATHER_API_KEY) {
      console.error("OpenWeatherMap API key not found.");
      return null;
    }
  
    const targetOpCode = v2LayerMap[layerType];
    if (!targetOpCode) return null;
  
    try {
      return new window.google.maps.ImageMapType({
        getTileUrl: function (coord, zoom) {
          // CORRECTED URL: Using the 'maps' endpoint which supports forecasts, with https, and no '.png'
          let tileUrl = `https://maps.openweathermap.org/maps/2.0/weather/${targetOpCode}/${zoom}/${coord.x}/${coord.y}?appid=${process.env.REACT_APP_OPENWEATHER_API_KEY}`;
          if (forecastOffset > 0) {
            const forecastTimestamp = dayjs().add(forecastOffset, 'hour').unix();
            tileUrl += `&date=${forecastTimestamp}`;
          }
          if (targetOpCode === 'TA2') {
            tileUrl += '&fill_bound=true';
          }
          return tileUrl;
        },
        tileSize: new window.google.maps.Size(256, 256),
        opacity: opacity,
        name: `${weatherLayerOptions[layerType]} +${forecastOffset}h`,
      });
    } catch (error) {
      console.error("Error creating weather layer:", error);
      message.error(`Failed to load ${weatherLayerOptions[layerType] || 'weather'} data.`);
      return null;
    }
  }, [map, v2LayerMap, weatherLayerOptions]);

  const handleWeatherToggle = () => {
    if (showWeatherLayer) {
      // Turn OFF
      clearWeatherLayers();
      setShowWeatherLayer(false);
      setIsPlaying(false);
      message.info(`${weatherLayerOptions[weatherLayerType]} layer hidden.`);
    } else {
      // Turn ON
      setForecastHourOffset(0);
      setForecastStartTime(dayjs());
      setShowWeatherLayer(true);
      // The useEffect for forecastHourOffset will handle the initial layer creation
      message.success(`${weatherLayerOptions[weatherLayerType]} layer enabled.`);
    }
  };

  const handleWeatherTypeChange = (newType) => {
    clearWeatherLayers();
    setIsPlaying(false);
    setWeatherLayerType(newType);
    setForecastHourOffset(0);
    // The useEffect for forecastHourOffset will handle the layer creation
    message.success(`${weatherLayerOptions[newType]} layer enabled.`);
  };

  const handleSliderChange = (newValue) => {
    if (isPlaying) setIsPlaying(false);
    setForecastHourOffset(newValue);
  };


  useEffect(() => {
    if (isPlaying) {
      const interval = {
        precipitation_new: 1100, // Faster for the lighter precipitation layer
        temp_new: 1200,          // Slower for heavier full-screen layers
        wind_new: 1200,          // Slower for heavier full-screen layers
      }[weatherLayerType] || 1200; // Default to a safe value

      playIntervalRef.current = setInterval(() => {
        setForecastHourOffset(prevOffset => {
          const newOffset = prevOffset + 3;
          if (newOffset > 72) {
            setIsPlaying(false); // Stop at the end
            return 72;
          }

          // --- Animation & Preload Logic ---
          if (map) {
            // 1. Remove the old visible layer
            if (currentWeatherLayerRef.current) {
              for (let i = 0; i < map.overlayMapTypes.getLength(); i++) {
                if (map.overlayMapTypes.getAt(i) === currentWeatherLayerRef.current) {
                  map.overlayMapTypes.removeAt(i);
                  break;
                }
              }
            }

            // 2. The preloaded layer is now the current one
            currentWeatherLayerRef.current = preloadWeatherLayerRef.current;
            if (currentWeatherLayerRef.current) {
              currentWeatherLayerRef.current.setOpacity(0.8);
            }

            // 3. Preload the *next* layer
            const preloadOffset = newOffset + 3;
            if (preloadOffset <= 72) {
              const nextLayer = createWeatherLayer(weatherLayerType, preloadOffset, 0); // Start with opacity 0
              if (nextLayer) {
                map.overlayMapTypes.push(nextLayer);
                preloadWeatherLayerRef.current = nextLayer;
              }
            } else {
              preloadWeatherLayerRef.current = null;
            }
          }
          // --- End Animation Logic ---

          return newOffset;
        });
      }, interval);
    } else {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    }
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, [isPlaying, map, weatherLayerType, createWeatherLayer]);

  // Effect for handling manual slider changes or initial setup
  useEffect(() => {
    if (!showWeatherLayer || !map || isPlaying) {
      return;
    }

    // This is for manual control, so we reset everything
    clearWeatherLayers();

    // Create and show the layer for the current offset
    const currentLayer = createWeatherLayer(weatherLayerType, forecastHourOffset, 0.8);
    if (currentLayer) {
      map.overlayMapTypes.push(currentLayer);
      currentWeatherLayerRef.current = currentLayer;
    }

    // Preload the next step
    const preloadOffset = forecastHourOffset + 3;
    if (preloadOffset <= 72) {
      const preloadLayer = createWeatherLayer(weatherLayerType, preloadOffset, 0); // Opacity 0
      if (preloadLayer) {
        map.overlayMapTypes.push(preloadLayer);
        preloadWeatherLayerRef.current = preloadLayer;
      }
    }
  }, [forecastHourOffset, showWeatherLayer, weatherLayerType, map, isPlaying, createWeatherLayer, clearWeatherLayers]);


  useEffect(() => {
    if (map && Array.isArray(mapShipmentData) && mapShipmentData.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
  
      mapShipmentData.forEach((point) => {
        if (
          point?.latest_location?.latest_lat &&
          point?.latest_location?.latest_long
        ) {
          const lat = parseFloat(point.latest_location.latest_lat);
          const lng = parseFloat(point.latest_location.latest_long);
  
          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            bounds.extend({ lat, lng });
          }
        }
      });
  
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
        // Add padding to the bounds
        const listener = window.google.maps.event.addListenerOnce(
          map,
          "idle",
          () => {
          if (map.getZoom() > 15) {
            map.setZoom(15);
          }
          }
        );
      }
    }
  }, [map, mapShipmentData]);
  
  const getTagStyles = (min, max, current) => {
    if (current === null || current === undefined || current === "") {
      return {
        backgroundColor: "#F3F3F3", // light gray
        color: "#333333", // dark gray text
      };
    }

    const numericCurrent = Number(current);
    const numericMin = typeof min === "number" ? min : undefined;
    const numericMax = typeof max === "number" ? max : undefined;

    // Case: Exact match with min or max
    if (
      (numericMin !== undefined && numericCurrent === numericMin) ||
      (numericMax !== undefined && numericCurrent === numericMax)
    ) {
      return {
        backgroundColor: "#EAF6ED", // green bg
        color: "#0E8345", // green text
      };
    }

    // Case: Both min and max are defined
    if (numericMin !== undefined && numericMax !== undefined) {
      const range = numericMax - numericMin;
      const lowerBound = numericMin + range * 0.2;
      const upperBound = numericMax - range * 0.2;

      if (numericCurrent < numericMin || numericCurrent > numericMax) {
        return {
          backgroundColor: "#FFF0EE", // light red bg
          color: "#BB032A", // red text
        };
      } else if (numericCurrent < lowerBound || numericCurrent > upperBound) {
        return {
          backgroundColor: "#FDF2DC", // light orange bg
          color: "#9F6402", // orange text
        };
      } else {
        return {
          backgroundColor: "#EAF6ED", // green bg
          color: "#0E8345", // green text
        };
      }
    }

    // Case: Single value provided (min or max only)
    const reference = numericMin ?? numericMax;

    if (typeof reference === "number") {
      const diff = Math.abs(numericCurrent - reference);
      const threshold = reference * 0.2;

      if (numericCurrent === reference) {
        return {
          backgroundColor: "#EAF6ED",
          color: "#0E8345",
        };
      } else if (diff <= threshold) {
        return {
          backgroundColor: "#FDF2DC", // light orange
          color: "#9F6402", // orange text
        };
      } else {
        return {
          backgroundColor: "#FFF0EE", // light red
          color: "#BB032A", // red text
        };
      }
    }

    // Default fallback
    return {
      backgroundColor: "#EAF6ED",
      color: "#0E8345",
    };
  };

  const getBatteryTagStyles = (battery) => {
    if (battery === null || battery === undefined || battery === "") {
      return {
        backgroundColor: "#F3F3F3", // light gray for no value
        color: "#333333", // dark gray text
      };
    }

    if (battery <= 10) {
      return {
        backgroundColor: "#FFF0EE", // light red
        color: "#BB032A", // red text
      };
    }

    if (battery <= 20) {
      return {
        backgroundColor: "#FDF2DC", // light orange
        color: "#9F6402", // orange text
      };
    }

    return {
      backgroundColor: "#EAF6ED", // light green
      color: "#0E8345", // green text
    };
  };

  const getVibilityWindowStyles = (
    current,
    status,
    scheduled_delivery_date
  ) => {
    if (!current || current === "00:00:00") {
      return {
        backgroundColor: "#f3f3f3", // light gray
        color: "#333333", // dark gray
      };
    }

    const now = new Date();
    const scheduledDate = new Date(scheduled_delivery_date);

    const isDelayedAndDelivered =
      status?.toLowerCase() !== "delivered" && now > scheduledDate;

    if (isDelayedAndDelivered) {
      return {
        backgroundColor: "#FFF0EE", // light red
        color: "#BB032A", // red
      };
    }

    return {
      backgroundColor: "#EAF6ED", // light green
      color: "#0E8345", // green
    };
  };


  const getColoredMarker = (type, color, hasAlert, isCritical ) => {
    const airSVG = `
 <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="16" fill="${color}"/>
<path d="M17.9998 14.6666H21.3337C22.0698 14.6668 22.6666 15.2635 22.6667 15.9996C22.6667 16.7359 22.07 17.3334 21.3337 17.3336H17.9998L13.9998 21.3336H12.6667L14.8894 17.3336H11.3337C10.5974 17.3336 9.99976 16.736 9.99976 15.9996C9.99976 15.9921 10.0006 15.9846 10.0007 15.9772L9.43335 12.6666H9.69312C10.0609 12.6666 10.3995 12.8683 10.574 13.192L11.3679 14.6666H14.8894L12.6667 10.6666H13.9998L17.9998 14.6666Z" fill="white"/>
</svg>`;

const airRiskSVG = `
<svg width="39" height="38" viewBox="0 0 39 38" fill="none" xmlns="http://www.w3.org/2000/svg">
<g filter="url(#filter0_d_17010_25183)">
<rect y="6" width="32" height="32" rx="16" fill="${color}"/>
<path d="M17.9995 20.6666H21.3335C22.0696 20.6668 22.6663 21.2635 22.6665 21.9996C22.6665 22.7359 22.0697 23.3334 21.3335 23.3336H17.9995L13.9995 27.3336H12.6665L14.8892 23.3336H11.3335C10.5971 23.3336 9.99951 22.736 9.99951 21.9996C9.99951 21.9921 10.0004 21.9846 10.0005 21.9772L9.43311 18.6666H9.69287C10.0606 18.6666 10.3993 18.8683 10.5737 19.192L11.3677 20.6666H14.8892L12.6665 16.6666H13.9995L17.9995 20.6666Z" fill="white"/>
</g>
<path d="M29.5405 2.35315L36.8745 15.0201L37.4175 15.9586H20.5825L21.1255 15.0201L28.4585 2.35315L28.9995 1.41956L29.5405 2.35315ZM28.9585 12.7086H29.0415V12.6246H28.9585V12.7086ZM28.9585 10.0416H29.0415V8.62463H28.9585V10.0416Z" fill="#E6A000" stroke="white" stroke-width="1.25"/>
<defs>
<filter id="filter0_d_17010_25183" x="-16" y="-6" width="64" height="64" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="4"/>
<feGaussianBlur stdDeviation="8"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17010_25183"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17010_25183" result="shape"/>
</filter>
</defs>
</svg>
`
const airCriticalSVG = `
<svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
<g filter="url(#filter0_d_17001_25054)">
<rect y="5" width="32" height="32" rx="16" fill="${color}"/>
<path d="M17.9995 19.6666H21.3335C22.0696 19.6668 22.6663 20.2635 22.6665 20.9996C22.6665 21.7359 22.0697 22.3334 21.3335 22.3336H17.9995L13.9995 26.3336H12.6665L14.8892 22.3336H11.3335C10.5971 22.3336 9.99951 21.736 9.99951 20.9996C9.99951 20.9921 10.0004 20.9846 10.0005 20.9772L9.43311 17.6666H9.69287C10.0606 17.6666 10.3993 17.8683 10.5737 18.192L11.3677 19.6666H14.8892L12.6665 15.6666H13.9995L17.9995 19.6666Z" fill="white"/>
<rect x="21" width="16" height="16" rx="8" fill="white"/>
<path d="M29.0002 1.33337C25.3202 1.33337 22.3335 4.32004 22.3335 8.00004C22.3335 11.68 25.3202 14.6667 29.0002 14.6667C32.6802 14.6667 35.6668 11.68 35.6668 8.00004C35.6668 4.32004 32.6802 1.33337 29.0002 1.33337ZM29.6668 11.3334H28.3335V10H29.6668V11.3334ZM29.6668 8.66671H28.3335V4.66671H29.6668V8.66671Z" fill="#DE1135"/>
</g>
<defs>
<filter id="filter0_d_17001_25054" x="-16" y="-12" width="69" height="69" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="4"/>
<feGaussianBlur stdDeviation="8"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17001_25054"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17001_25054" result="shape"/>
</filter>
</defs>
</svg>
`


    const truckSVG = `
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="32" height="32" rx="16" fill="${color}"/>
<path d="M19.333 13.333H21.333L23.333 16V19.333H21.7119L21.7031 19.5049C21.6171 20.3532 20.9003 21.0156 20.0293 21.0156C19.1584 21.0154 18.4424 20.3531 18.3564 19.5049L18.3477 19.333H13.6816L13.6729 19.5049C13.5868 20.3531 12.871 21.0156 12 21.0156C11.129 21.0156 10.4132 20.3531 10.3271 19.5049L10.3184 19.333H8.66699V12C8.66699 11.6333 8.79748 11.3197 9.05859 11.0586C9.3197 10.7975 9.63333 10.667 10 10.667H19.333V13.333ZM19.333 14.667V16.667H22.167L20.667 14.667H19.333Z" fill="white"/>
</svg>`;
    const truckCriticalSVG = `
 <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
<g filter="url(#filter0_d_17001_25041)">
<rect y="5" width="32" height="32" rx="16" fill="${color}"/>
<path d="M19.333 18.333H21.333L23.333 21V24.333H21.7119L21.7031 24.5049C21.6171 25.3532 20.9003 26.0156 20.0293 26.0156C19.1584 26.0154 18.4424 25.3531 18.3564 24.5049L18.3477 24.333H13.6816L13.6729 24.5049C13.5868 25.3531 12.871 26.0156 12 26.0156C11.129 26.0156 10.4132 25.3531 10.3271 24.5049L10.3184 24.333H8.66699V17C8.66699 16.6333 8.79748 16.3197 9.05859 16.0586C9.3197 15.7975 9.63333 15.667 10 15.667H19.333V18.333ZM19.333 19.667V21.667H22.167L20.667 19.667H19.333Z" fill="white"/>
<rect x="21" width="16" height="16" rx="8" fill="white"/>
<path d="M29.0002 1.33337C25.3202 1.33337 22.3335 4.32004 22.3335 8.00004C22.3335 11.68 25.3202 14.6667 29.0002 14.6667C32.6802 14.6667 35.6668 11.68 35.6668 8.00004C35.6668 4.32004 32.6802 1.33337 29.0002 1.33337ZM29.6668 11.3334H28.3335V10H29.6668V11.3334ZM29.6668 8.66671H28.3335V4.66671H29.6668V8.66671Z" fill="#DE1135"/>
</g>
<defs>
<filter id="filter0_d_17001_25041" x="-16" y="-12" width="69" height="69" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="4"/>
<feGaussianBlur stdDeviation="8"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17001_25041"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17001_25041" result="shape"/>
</filter>
</defs>
</svg>
`;
    const truckRiskSVG = `
  <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
<g filter="url(#filter0_d_17007_25152)">
<rect y="6" width="32" height="32" rx="16" fill="${color}"/>
<path d="M19.333 19.333H21.333L23.333 22V25.333H21.7119L21.7031 25.5049C21.6171 26.3532 20.9003 27.0156 20.0293 27.0156C19.1584 27.0154 18.4424 26.3531 18.3564 25.5049L18.3477 25.333H13.6816L13.6729 25.5049C13.5868 26.3531 12.871 27.0156 12 27.0156C11.129 27.0156 10.4132 26.3531 10.3271 25.5049L10.3184 25.333H8.66699V18C8.66699 17.6333 8.79748 17.3197 9.05859 17.0586C9.3197 16.7975 9.63333 16.667 10 16.667H19.333V19.333ZM19.333 20.667V22.667H22.167L20.667 20.667H19.333Z" fill="white"/>
<path d="M29.5405 2.35315L36.8745 15.0201L37.4175 15.9586H20.5825L21.1255 15.0201L28.4585 2.35315L28.9995 1.41956L29.5405 2.35315ZM28.9585 12.7086H29.0415V12.6246H28.9585V12.7086ZM28.9585 10.0416H29.0415V8.62463H28.9585V10.0416Z" fill="#E6A000" stroke="white" stroke-width="1.25"/>
</g>
<defs>
<filter id="filter0_d_17007_25152" x="-16" y="-11.8282" width="70.5015" height="69.8282" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="4"/>
<feGaussianBlur stdDeviation="8"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17007_25152"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17007_25152" result="shape"/>
</filter>
</defs>
</svg>
`;

    // const svg = type === "air" ? hasAlert ? isCritical ? airCriticalSVG : airRiskSVG : airSVG : hasAlert ? isCritical ? truckCriticalSVG  : truckRiskSVG :  truckSVG;

    let svg;

if (type === "air") {
  if (hasAlert) {
    svg = isCritical ? airCriticalSVG : airRiskSVG;
  } else {
    svg = airSVG;
  }
} else {
  if (hasAlert) {
    svg = isCritical ? truckCriticalSVG : truckRiskSVG;
  } else {
    svg = truckSVG;
  }
}

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };


  const truncateByLength = (text, maxLength = 23) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  return (
    <div className="position-relative">
      <style>
        {`
          .map-filter-wrapper .ant-select-selector {
            border-radius: 40px !important;
          }
          
          .map-filter-wrapper .cloud-weather-button.ant-btn-default:not(:disabled):hover,
          .map-filter-wrapper .cloud-weather-button.ant-btn-default:not(:disabled):focus,
          .map-filter-wrapper .cloud-weather-button.ant-btn-default:not(:disabled):active {
            background-color: #f5f5f5 !important;
            color: inherit !important; // Prevents icon/text color change
            border-color: transparent !important;
            box-shadow: none !important; // Attempt to remove any shadow
          }

          .bottom-slider-panel {
            position: absolute;
            bottom: 2rem;
            left: 50%;
            transform: translateX(-50%);
            /* New styles from reference */
            display: inline-flex;
            gap: 8px; /* gap-2 */
            align-items: center;
            padding: 4px 8px; /* px-2 py-1 */
            height: 44px; /* h-11 */
            background-color: white;
            border-radius: 8px; /* rounded-lg */
            border: 1px solid rgba(0, 0, 0, 0.1);
            box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); /* shadow-sm */
            opacity: 0.95;
            width: 580px; 
            z-index: 10;
          }
          
          .play-pause-button {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 36px; /* w-9 */
            height: 36px; /* h-9 */
            border: none;
            background: transparent;
            cursor: pointer;
            color: #000;
          }

          .slider-and-labels-container {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            gap: 8px; /* more space between slider and labels */
          }

          .forecast-slider-wrapper {
            position: relative;
            height: 4px; /* h-1 */
            cursor: pointer;
            width: 100%;
            z-index: 2;
          }
          .forecast-slider-track-background, .forecast-slider-track-progress {
            position: absolute;
            top: 0;
            height: 100%;
            border-radius: 9999px; /* rounded-full */
          }
          .forecast-slider-track-background {
            width: 100%;
            background-color: #f1f5f9; /* zinc-100 */
          }
          .forecast-slider-track-progress {
            background-color: #2563eb; /* blue-600 */
          }
          .forecast-slider-handle {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 14px;  /* w-3.5 */
            height: 14px; /* h-3.5 */
            background-color: white;
            border: 2px solid #2563eb; /* border-2 border-blue-600 */
            border-radius: 999px; /* rounded-full */
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
            z-index: 2;
          }

          .forecast-scrubber-container {
            position: absolute;
            bottom: 20px;
            transform: translateX(-50%);
            display: flex;
            flex-direction: column;
            align-items: center;
            z-index: 3;
            pointer-events: none; /* Make it non-interactive */
          }
          .scrubber-box {
            padding: 4px 8px;
            background-color: #1e40af; /* blue-800 */
            color: white;
            border-radius: 6px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
            font-size: 12px;
            font-weight: 500;
            line-height: 1;
            white-space: nowrap;
            opacity: 0.9;
          }
          .scrubber-line {
            width: 2px;
            height: 8px;
            background-color: #1e40af;
            opacity: 0.9;
          }

          .forecast-slider-labels {
            display: flex;
            justify-content: space-between;
          }

          .forecast-slider-label {
            font-size: 9px; /* Smaller text */
            line-height: 1.2;
            color: #52525b; /* zinc-600 */
            white-space: nowrap;
          }

          /* Remove old slider styles to prevent conflicts */
          .custom-slider-container, .slider-track-wrapper, .slider-handle, .slider-labels, .slider-label {
            display: none;
          }

        `}
      </style>
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#B6B6B680",
            position: "absolute",
            zIndex: "12",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* <Spin size="large" /> */}
        </div>
      ) : isShipments.length === 0 ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#B6B6B680",
            position: "absolute",
            zIndex: "12",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <HomeEmptyShipments allPermissions={allPermissions} />
        </div>
      ) : null}

      <div
        className="map-filter-wrapper"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          position: "absolute",
          top: "1rem",
          left: "1rem",
          zIndex: 5,
        }}
      >
        {productPermissions && (
          <HomeMapMultiSelectDropdown
            triggerButton={
              <Button className={`home-map-dropdown-button ${filterQuery?.products?.length > 0 && 'on-select' } `} >
                <HomeProductIcon />
              </Button>
            }
            options={productList.filter(
              (item) => item?.title || item?.name || item?.id
            )}
            title={"Product"}
            selectedIds={filterQuery?.products || []}
            onApply={handleProductApply}
            showColors={false}
            getColor={(option) => option.color}
          />
        )}
        {hasValidTeams && teamsPermissions ? (
          <HomeMapMultiSelectDropdown
            triggerButton={
              <Button className={`home-map-dropdown-button ${filterQuery?.teams?.length > 0 && 'on-select' } `} >
                <HomeTeamsIcon />
              </Button>
            }
            options={allTeams}
            title={"Team"}
            selectedIds={filterQuery?.teams || []}
            onApply={handleTeamsApply}
            showColors={false}
            getColor={(option) => option.color}
          />
        ) : null}
        <HomeMapMultiSelectDropdown
        dropdownWidth={320}
          triggerButton={
            <Button className={`home-map-dropdown-button ${filterQuery?.temprature?.length > 0 && 'on-select' } `} >
              <HomeTemperatureIcon />
            </Button>
          }
          options={HometempratureList}
          title={"Temperature"}
          selectedIds={filterQuery?.temprature || []}
          onApply={handleTempratureApply}
          showColors
          getColor={(option) => option.color}
        />
        <HomeMapMultiSelectDropdown
          triggerButton={
            <Button className={`home-map-dropdown-button ${filterQuery?.risk?.length > 0 && 'on-select' } `} >
              <HomeRiskIcon />
            </Button>
          }
          options={RiskLevelList}
          title={"Risk"}
          selectedIds={filterQuery?.risk || []}
          onApply={handleRiskApply}
          showColors
          getColor={(option) => option.color}
        />
      </div>

      {/* {productPermissions && ( */}
      {/* <CustomSelect
        placeholder="All products"
        onChange={handleFilterProducts}
        value={selectedProduct}
        fontSize={10}
        lineHeight={"16px"}
        fontWeight="600"
        border="0px"
        bgColor={"#F3F3F3"}
        borderRadius={"40px"}
        boxShadow={"none"}
        height="28px"
        width="140px"
        options={productList}
      /> */}
      {/* )}     */}

      {/* <div className="map-weather-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'absolute', top: '1rem', right: '1rem', zIndex: 5 }}> */}
      <div className="map-weather-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', position: 'absolute', top: '1rem', right: '1rem', zIndex: 5, backgroundColor: 'transparent' }}>
              
              <Space.Compact style={{borderRadius: showWeatherLayer ?'40px':'8px', overflow: showWeatherLayer ?'hidden':'visible', boxShadow: 'none', backgroundColor: 'transparent', boxShadow: '0px 4px 16px 0px #0000001F',}}>
          <Button
           icon={
            // <HomeCloudIcon />
            <CloudOutlined/>
          }
            title={showWeatherLayer ? `Hide ${weatherLayerOptions[weatherLayerType]}` : `Show ${weatherLayerOptions[weatherLayerType] || 'Weather'}`}
            onClick={handleWeatherToggle}
            type={"default"}
            className="cloud-weather-button"
            style={{
              width: '32px',
              height: '32px',
              backgroundColor:   '#fff',
              border: '0px',
              color: showWeatherLayer ? '#fff' : '#fff',
              borderRadius: showWeatherLayer ? '40px 0 0 40px' : '8px',
            }}
          />
          {showWeatherLayer && (
            <Select
              value={weatherLayerType}
              onChange={handleWeatherTypeChange}
              style={{
                width: 150,
                backgroundColor: '#fff',
                border: '0px',
                boxShadow: 'none',
                height: '32px',
                borderRadius: '0 40px 40px 0'
              }}
              className={showWeatherLayer ? "weather-select-active" : ""}
              disabled={!isLoaded || !map}
            >
              {Object.entries(weatherLayerOptions).map(([key, name]) => (
                <Select.Option key={key} value={key}>{name}</Select.Option>
              ))}
            </Select>
          )}
        </Space.Compact>
        
        {/* WeatherLegend is now here, will appear under the controls when active */}
        {showWeatherLayer && weatherLayerType && (
          <WeatherLegend
            activeLayerType={weatherLayerType}
            weatherLayerOptions={weatherLayerOptions}
          />
        )}
      </div>

      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          options={{
            styles: mapStyles,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControlOptions: {
              position: window.google.maps.ControlPosition.BOTTOM_RIGHT, // Change position here
            },
          }}
          onLoad={onLoad}
          zoom={6}
        >
          {mapShipmentData?.length > 0 && (
            <>
              <MarkerClusterer
              
                options={{
                  imagePath:
                    "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
                  minimumClusterSize: 2,
                  maxZoom: 15,
                  gridSize: 50,
                  styles: [
                    {
                      textColor: "white",
                      textSize: 18,
                      url: clusterImg,
                      height: 44,
                      width: 44,
                    },
                    {
                      textColor: "white",
                      textSize: 14,
                      url: clusterImg,
                      height: 56,
                      width: 56,
                    },
                    {
                      textColor: "white",
                      textSize: 14,
                      url: clusterImg,
                      height: 66,
                      width: 66,
                    },
                  ],
                }}
              >
                {(clusterer) =>
                  mapShipmentData.map((point, idx) => {
                    const baseLat = parseFloat(
                      point.latest_location.latest_lat
                    );
                    const baseLng = parseFloat(
                      point.latest_location.latest_long
                    );
                    
                    // Create a small offset for markers at the same location
                    const offset = 0.0001; // Approximately 11 meters
                    const lat =
                      baseLat + offset * Math.sin((idx * Math.PI) / 4);
                    const lng =
                      baseLng + offset * Math.cos((idx * Math.PI) / 4);

                    return (
                      <React.Fragment key={point.shipment_id}>
                        <Marker
                          position={{ lat, lng }}
                          clusterer={clusterer}
                          onClick={() => getCurrentPointInfo(point)}
                          // icon={
                          //   point?.legs && point?.legs?.mode === "air"
                          //     ? airMarker
                          //     : markerSize
                          // }
                          icon={getColoredMarker(
                            point.mode,
                            point?.icon_color || "#266EF1",
                            point?.has_alert,
                            point?.alert?.is_critical
                          )}
                        >

                        {/* {point?.has_alert && (
                          <OverlayView
                            position={{ lat, lng }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                            <div style={{ position: "relative" }}>
                              <span
                                style={{
                                  position: "absolute",
                                  bottom: "22px",
                                  left: "5px",
                                  borderRadius: "50px",
                                }}
                              >
                                {point?.alert?.is_critical === true ? (
                                  <svg
                                  width="17"
                                    height="17"
                                    viewBox="0 0 17 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <rect
                                      x="0.0273438"
                                      y="0.166992"
                                      width="16"
                                      height="16"
                                      rx="8"
                                      fill="white"
                                    />
                                    <path
                                      d="M8.02702 1.5C4.34702 1.5 1.36035 4.48667 1.36035 8.16667C1.36035 11.8467 4.34702 14.8333 8.02702 14.8333C11.707 14.8333 14.6937 11.8467 14.6937 8.16667C14.6937 4.48667 11.707 1.5 8.02702 1.5ZM8.69369 11.5H7.36035V10.1667H8.69369V11.5ZM8.69369 8.83333H7.36035V4.83333H8.69369V8.83333Z"
                                      fill="#DE1135"
                                      />
                                  </svg>
                                ) : (
                                  <svg
                                    width="20"
                                    height="17"
                                    viewBox="0 0 20 17"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    >
                                    <path
                                      d="M10.5405 2.35318L17.8745 15.0202L17.3335 15.9586H2.6665L2.12549 15.0202L9.4585 2.35318H10.5405ZM9.9585 12.7086H10.0415V12.6247H9.9585V12.7086ZM9.9585 10.0417H10.0415V8.62466H9.9585V10.0417Z"
                                      fill="#E6A000"
                                      stroke="white"
                                      stroke-width="1.25"
                                      />
                                  </svg>
                                )}
                              </span>
                            </div>
                          </OverlayView>
                        )} */}
                </Marker>
                      </React.Fragment>
                    );
                  })
                }
              </MarkerClusterer>
            </>
          )}
          </GoogleMap>
      ) : (
        <h1 style={{ textAlign: "center", color: "#333" }}>Loading...</h1>
      )}

      <Tag
        style={{
          color: "#166C3B",
          position: "absolute",
          bottom: "1rem",
          left: "1rem",
          zIndex: "9",
          fontSize: "10px",
          fontWeight: "400",
          height: "22px",
          borderRadius: "5px",
          paddingLeft: 0,
          display: "flex",
        }}
        color="#EAF6ED"
      >
        <Tag
          style={{
            color: "#FFF",
            display: "flex",
            gap: "2px",
            alignItems: "center",
            width: "fit-content",
            zIndex: "9",
            fontSize: "10px",
            fontWeight: "600",
            height: "23px",
            borderRadius: "5px",
            margin:'-1px 7px 0px -1px',
          }}
          color="#0E8345"
        >
          {/* <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8 0.5C6.9 0.5 6.1 1.1 5.5 1.75C4.9 1.15 4.1 0.5 3 0.5C1.25 0.5 0 1.95 0 3.75C0 4.65 0.35 5.45 1 6L5.5 10.25L10 6C10.6 5.4 11 4.65 11 3.75C11 1.95 9.75 0.5 8 0.5Z" fill="#EAF6ED"/>
</svg> */}
          ⚪ LIVE
        </Tag>
        {badgeValue ? badgeValue : 0} shipments in transit
      </Tag>

      {showInfoModal && (
        <div onClick={closeInfoModal} className="overlay-transparent">
          <div
            onClick={(e) => e.stopPropagation()}
            className="custom-card-drawer home-map"
          >
            <Flex vertical={true} gap={14} style={{ width: "100%" }}>
              <Flex
                style={{
                  justifyContent: "space-between",
                  display: "flex",
                  alignItems: "center",
                }}
                gap={0}
                vertical={false}
                className="custom-card-drawer-header"
              >
                <Flex align={"center"} gap={8} vertical={false}>
                  <div tilt={shipment.reference_number}>
                    {/* <CustomTitle
                    text="Reference ID"
                    fontSize={14}
                    fontWeight={600}
                    lineHeight={"16px"}
                  /> */}
                    <Tooltip title={shipment.reference_number} placement="top">
                      <span>
                        <CustomTitle
                          text={
                            truncateByLength(shipment.reference_number, 19) ||
                            "N/A"
                          }
                          fontSize={15}
                          fontWeight={700}
                          lineHeight="24px"
                          color="#000"
                          marginBottom="0"
                          marginTop={0}
                        />
                      </span>
                    </Tooltip>
                  </div>
                  {/* <CustomButton
                  text="View full details"
                  width={138}
                  height={36}
                  borderRadius={14}
                  bgColor="#f3f3f3"
                  color="#000"
                  border="0px"
                  boxShadow={"none"}
                  fontSize={12}
                  fontWeight={500}
                  onClick={goToDetail}
                /> */}

                  {shipment?.teams?.length > 0 &&
                    (() => {
                      const nonDefaultTeams = shipment.teams.filter(
                        (team) => team.name.toLowerCase() !== "default"
                      );

                      if (nonDefaultTeams.length === 0) return null;

                      return (
                        <div className="flex white-space-nowrap">
                          <TeamBadge
                            className="mr-1"
                            color={nonDefaultTeams[0]?.color}
                            text={nonDefaultTeams[0]?.name}
                          />
                          {nonDefaultTeams.length > 1 && (
                            <Tooltip
                              title={nonDefaultTeams
                                .slice(1)
                                .map((team) => team.name)
                                .join(", ")}
                              placement="top"
                            >
                              <div
                                style={{
                                  display: "inline-block",
                                  cursor: "pointer",
                                }}
                              >
                                <TeamBadge
                                  color={null}
                                  text={`+ ${nonDefaultTeams.length - 1}`}
                                />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      );
                    })()}
                </Flex>

                <div style={{ display: "flex", gap: "16px" }}>
                  <span
                    onClick={goToDetail}
                    style={{
                      fontWeight: "700",
                      cursor: "pointer",
                      fontSize: "16px",
                      lineHeight: "16px",
                    }}
                  >
                <img src={doubleArrow} style={{width:'16px', height:'16px', marginTop:'-2px',  transform: 'rotate(130deg)' }} />

                  </span>
                  <span onClick={() => closeInfoModal()}>
                    {" "}
                    <CrossIcon />
                  </span>
                </div>
              </Flex>

              {alertsData?.length > 0 &&
                alertsData.map((alert, index) => {
                  return (
                    <React.Fragment key={alert.id}>
                      {alertIndex === index && (
                        <>
                          <StatusCardNotification
                            cardStyle={"style-1"}
                            icon={<WarningFilled />}
                            title={alert?.title || ""}
                            description={alert?.detail || ""}
                            createdAt={alert?.created_at || ""}
                            bgColor={
                              alert?.is_critical === false
                                ? "#F6BC2F"
                                : "#DE1135"
                            }
                            showButtons={false}
                            color={
                              alert?.is_critical === false ? "#000" : "#fff"
                            }
                            actionButtonWidth="0"
                            actionButtonBorderRadius="8px"
                            actionButtonBgColor={
                              alert?.is_critical === false
                                ? "#FBE5B6"
                                : "#BB032A"
                            }
                          // dismissAction={() =>
                          //   handleAlertResolved(false, alert?.id)
                          // }
                          // actionButtonOnClick={() =>
                          //   handleAlertResolved(true, alert?.id)
                          // }
                          />

                          {alertsData.length > 1 && (
                            <Flex
                              style={{ marginTop: "-5px" }}
                              justify="space-between"
                              align="center"
                              className="custom-card-drawer-header"
                            >
                              <p
                                style={{
                                  color: "#000",
                                  marginBottom: "0px",
                                  fontSize: "12px",
                                }}
                              >
                                {alertIndex + 1} of {alertsData.length} issues
                              </p>
                              <Flex
                                gap={8}
                                align="center"
                                className="custom-card-drawer-header-actions"
                              >
                                <button
                                  style={{ border: 0, borderRadius: "50px" }}
                                  title="Previous Alert"
                                  onClick={() => {
                                    if (alertIndex > 0) {
                                      setAlertIndex(alertIndex - 1); // Decrease the alertIndex by 1
                                    }
                                  }}
                                >
                                  <LeftOutlined
                                    style={{
                                      fontSize: "12px",
                                      color: index === 0 ? "#AFAFAF" : "#000",
                                    }}
                                  />
                                </button>

                                <button
                                  style={{ border: 0, borderRadius: "50px" }}
                                  title="Next Alert"
                                  onClick={() => {
                                    if (alertIndex < alertsData.length - 1) {
                                      setAlertIndex(alertIndex + 1); // Decrease the alertIndex by 1
                                    }
                                  }}
                                >
                                  <RightOutlined
                                    style={{
                                      fontSize: "12px",
                                      color:
                                        index === alertsData.length - 1
                                          ? "#AFAFAF"
                                          : "#000",
                                    }}
                                  />
                                </button>
                              </Flex>
                            </Flex>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

              <Flex vertical={true} gap={16}>
                <Row gap={24} justify="space-between">
                  <Col span={11}>
                    <CustomParagraph
                      text={"Delivery status"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <Tag
                      color={
                        shipment?.delivery_status
                          ?.toLowerCase()
                          .includes("on time") ||
                          shipment?.delivery_status?.toLowerCase() === "delivered"
                          ? "#EAF6ED"
                          : shipment?.delivery_status?.includes("Late")
                            ? "#FFF0EE"
                            : "rgb(243, 243, 243)"
                      }
                      style={{
                        borderRadius: "8px",
                        color:
                          shipment?.delivery_status?.toLowerCase() ===
                            "on time" ||
                            shipment?.delivery_status?.toLowerCase() ===
                            "delivered"
                            ? "#0E8345"
                            : shipment?.delivery_status?.includes("Late")
                              ? "#DE1135"
                              : "rgb(75, 75, 75)",
                        fontSize: "10px",
                      }}
                    >
                      {shipment?.delivery_status}
                    </Tag>
                  </Col>

                  <Col
                    title={
                      shipment?.type === 3
                        ? shipment?.actual_delivery_date
                        : shipment?.estimated_delivery_date
                    }
                    span={11}
                  >
                    <CustomParagraph
                      text={
                        shipment?.type === 3 ? "Delivered on" : "ZoomLogi ETA"
                      }
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="20px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(
                        shipment?.type === 3
                          ? shipment?.actual_delivery_date
                          : shipment?.estimated_delivery_date,
                        23
                      )}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>
                </Row>
                <Row gap={24} justify="space-between">
                  <Col title={shipment?.to_location?.address} span={11}>
                    <CustomParagraph
                      text={"Destination"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={shipment?.to_location?.address}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="20px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>

                  <Col title={shipment?.scheduled_delivery_date} span={11}>
                    <CustomParagraph
                      text={"Scheduled delivery"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.scheduled_delivery_date, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>
                </Row>

                <Row gap={24} justify="space-between">
                  <Col title={shipment?.courier?.title} span={11}>
                    <CustomParagraph
                      text={"Transport provider"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.courier?.title, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>

                  <Col title={shipment?.courier_tracking_id} span={11}>
                    <CustomParagraph
                      text={`${shipment?.courier?.title || ""} tracking ID`}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.courier_tracking_id, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>
                </Row>

                <Divider style={{ margin: "0" }} />

                <Row gap={24} justify="space-between">
                  <Col title={shipment?.product?.name} span={11}>
                    <CustomParagraph
                      text={"Product"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.product?.name, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>

                  <Col title={shipment?.product?.quantity} span={11}>
                    <CustomParagraph
                      text={"Product quantity"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.product?.quantity, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>
                </Row>

                <Row gap={24} justify="space-between">
                  <Col title={shipment?.product?.project_id} span={11}>
                    <CustomParagraph
                      text={"Project ID"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.product?.project_id, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>

                  <Col title={shipment?.product?.temperature_range} span={11}>
                    <CustomParagraph
                      text={"Temperature range"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(
                        shipment?.product?.temperature_range,
                        23
                      )}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>
                </Row>

                <Row gap={24} justify="space-between">
                  <Col title={shipment?.sensor?.type} span={11}>
                    <CustomParagraph
                      text={"Sensor"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.sensor?.type, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>

                  <Col title={shipment?.sensor?.id} span={11}>
                    <CustomParagraph
                      text={"Sensor ID"}
                      fontSize={12}
                      fontWeight={600}
                      lineHeight="16px"
                      color="#000"
                      marginBottom="4px"
                    />

                    <CustomParagraph
                      text={truncateText(shipment?.sensor?.id, 23)}
                      fontSize={12}
                      fontWeight={400}
                      lineHeight="16px"
                      color="#4B4B4B"
                      marginBottom="0"
                    />
                  </Col>
                </Row>

                <Flex
                  gap={8}
                  style={{ flexWrap: "wrap", marginBottom: "20px" }}
                >
                  <Tooltip
                    placement="top"
                    title={
                      <p
                        className="fw-semibold"
                        dangerouslySetInnerHTML={{
                          __html: `<span>Temperature sensor reading  </span> <br/> ${shipment?.monitor_info?.timestamp || ""
                            } `,
                        }}
                      />
                    }
                    arrow={true}
                  >
                    <Tag
                      icon={
                        <TempratureRangeIcon
                          style={{ fill: "#ffcc00", width: 14, height: 14 }}
                        />
                      }
                      style={{
                        margin: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gridGap: "3px",
                        cursor: "pointer",
                        height: "20px",
                        padding: "0 4px",
                        minWidth: "20px",
                        fontWeight: "600",
                        backgroundColor: "#EAF6ED",
                        color: "#0E8345",
                        border: "none",
                        ...getTagStyles(
                          shipment?.product?.temperature_min,
                          shipment?.product?.temperature_max,
                          shipment?.monitor_info?.temperature
                        ),
                      }}
                    // onClick={handleTempratureModal}
                    >
                      {/* {shipment?.monitor_info?.temperature
                        ? parseInt(shipment?.monitor_info?.temperature) || "--"
                        : "--"}
                      °C */}
                      {shipment?.monitor_info?.temperature != null && !isNaN(parseInt(shipment.monitor_info.temperature))
                        ? parseInt(shipment.monitor_info.temperature)
                        : '--'}°C
                    </Tag>
                  </Tooltip>
                  <Tooltip
                    placement="top"
                    title={
                      <p
                        className="fw-semibold"
                        dangerouslySetInnerHTML={{
                          __html: `<span>Sensor battery life</span>  <br/> ${shipment?.monitor_info?.timestamp || ""
                            } `,
                        }}
                      />
                    }
                    arrow={true}
                  >
                    <Tag
                      icon={
                        <BatteryIcon
                          style={{ fill: "#ffcc00", width: 14, height: 14 }}
                        />
                      }
                      style={{
                        margin: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gridGap: "3px",
                        cursor: "pointer",
                        height: "20px",
                        fontWeight: "600",
                        padding: "0 4px",
                        minWidth: "20px",
                        backgroundColor: "#EAF6ED",
                        border: "none",
                        color: "#0E8345",
                        ...getBatteryTagStyles(shipment?.monitor_info?.battery),
                      }}
                    // onClick={handleBatteryModal}
                    >
                      {/* {shipment?.monitor_info?.battery
                        ? parseInt(shipment?.monitor_info?.battery) || "--"
                        : "--"}
                      % */}
                      {shipment?.monitor_info?.battery != null && !isNaN(parseInt(shipment.monitor_info.battery))
                        ? parseInt(shipment.monitor_info.battery)
                        : '--'}%
                    </Tag>
                  </Tooltip>
                  {shipment?.product?.light && (
                    <Tooltip
                      placement="top"
                      title={
                        <p
                          className="fw-semibold"
                          dangerouslySetInnerHTML={{
                            __html: `<span>Light sensor reading</span> <br/> ${shipment?.monitor_info?.timestamp || ""
                              } `,
                          }}
                        />
                      }
                      arrow={true}
                    >
                      <Tag
                        icon={
                          <LightIcon
                            style={{ fill: "#ffcc00", width: 14, height: 14 }}
                          />
                        }
                        style={{
                          margin: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gridGap: "3px",
                          cursor: "pointer",
                          height: "20px",
                          fontWeight: "600",
                          padding: "0 4px",
                          minWidth: "20px",
                          backgroundColor: "#EAF6ED",
                          border: "none",
                          color: "#0E8345",
                          ...getTagStyles(
                            "",
                            shipment?.product?.light,
                            shipment?.monitor_info?.light
                          ),
                        }}
                      // onClick={handleLightModal}
                      >
                        {/* {shipment?.monitor_info?.light
                          ? parseInt(shipment?.monitor_info?.light) || "--"
                          : "--"}
                        lx */}
                        {shipment?.monitor_info?.light != null && !isNaN(parseInt(shipment.monitor_info.light))
                          ? parseInt(shipment.monitor_info.light)
                          : '--'}lx


                      </Tag>
                    </Tooltip>
                  )}
                  {shipment?.product?.humidity_max &&
                    shipment?.product?.humidity_min ? (
                    <Tooltip
                      placement="top"
                      title={
                        <p
                          className="fw-semibold"
                          dangerouslySetInnerHTML={{
                            __html: `<span>Humidity sensor reading</span> <br/> ${shipment?.monitor_info?.timestamp || ""
                              } `,
                          }}
                        />
                      }
                      arrow={true}
                    >
                      <Tag
                        icon={
                          <HumidityIcon
                            style={{ fill: "#ffcc00", width: 14, height: 14 }}
                          />
                        }
                        style={{
                          margin: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gridGap: "3px",
                          cursor: "pointer",
                          height: "20px",
                          padding: "0 4px",
                          minWidth: "20px",
                          fontWeight: "600",
                          backgroundColor: "#EAF6ED",
                          border: "none",
                          color: "#0E8345",
                          ...getTagStyles(
                            shipment?.product?.humidity_min,
                            shipment?.product?.humidity_max,
                            shipment?.monitor_info?.humidity
                          ),
                        }}
                      // onClick={handleHumidityModal}
                      >
                        {/* {shipment?.monitor_info?.humidity
                          ? parseInt(shipment?.monitor_info?.humidity) || "--"
                          : "--"}
                        % */}
                        {shipment?.monitor_info?.humidity != null && !isNaN(parseInt(shipment.monitor_info.humidity))
                          ? parseInt(shipment.monitor_info.humidity)
                          : '--'}%

                      </Tag>
                    </Tooltip>
                  ) : null}
                  {shipment?.product?.tilt && (
                    <Tooltip
                      placement="top"
                      title={
                        <p
                          className="fw-semibold"
                          dangerouslySetInnerHTML={{
                            __html: `<span>Tilt sensor reading</span> <br/> ${shipment?.monitor_info?.timestamp || ""
                              } `,
                          }}
                        />
                      }
                      arrow={true}
                    >
                      <Tag
                        icon={
                          <TiltIcon
                            style={{ fill: "#ffcc00", width: 14, height: 14 }}
                          />
                        }
                        style={{
                          margin: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gridGap: "3px",
                          cursor: "pointer",
                          height: "20px",
                          fontWeight: "600",
                          padding: "0 4px",
                          minWidth: "20px",
                          backgroundColor: "#EAF6ED",
                          border: "none",
                          color: "#0E8345",
                          ...getTagStyles(
                            "",
                            shipment?.product?.tilt,
                            shipment?.monitor_info?.tilt
                          ),
                        }}
                      >
                        {/* {shipment?.monitor_info?.tilt
                          ? parseInt(shipment?.monitor_info?.tilt) || "--"
                          : "--"}
                        °% */}
                        {shipment?.monitor_info?.tilt != null && !isNaN(parseInt(shipment.monitor_info.tilt))
                          ? parseInt(shipment.monitor_info.tilt)
                          : '--'}°

                      </Tag>
                    </Tooltip>
                  )}
                  {shipment?.product?.pressure_max &&
                    shipment?.product?.pressure_min ? (
                    <Tooltip
                      placement="top"
                      title={
                        <p
                          className="fw-semibold"
                          dangerouslySetInnerHTML={{
                            __html: `<span>Pressure sensor reading</span> <br/> ${shipment?.monitor_info?.timestamp || ""
                              } `,
                          }}
                        />
                      }
                      arrow={true}
                    >
                      <Tag
                        icon={
                          <PressureIcon
                            style={{ fill: "#ffcc00", width: 14, height: 14 }}
                          />
                        }
                        style={{
                          margin: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gridGap: "3px",
                          cursor: "pointer",
                          height: "20px",
                          fontWeight: "600",
                          padding: "0 4px",
                          minWidth: "20px",
                          backgroundColor: "#EAF6ED",
                          border: "none",
                          color: "#0E8345",
                          ...getTagStyles(
                            shipment?.product?.pressure_min,
                            shipment?.product?.pressure_max,
                            shipment?.monitor_info?.pressure
                          ),
                        }}
                      >
                        {/* {shipment?.monitor_info?.pressure
                          ? parseInt(shipment?.monitor_info?.pressure) || "--"
                          : "--"}{" "}
                        {shipment?.product?.pressure_unit} */}
                        {shipment?.monitor_info?.pressure != null && !isNaN(parseInt(shipment.monitor_info.pressure))
                          ? parseInt(shipment.monitor_info.pressure)
                          : '--'}  {shipment?.product?.pressure_unit}
                      </Tag>
                    </Tooltip>
                  ) : null}
                  <Tooltip
                    placement="top"
                    title={
                      <p
                        className="fw-semibold"
                        dangerouslySetInnerHTML={{
                          __html: "<span>Viable transit time remaining</span>",
                        }}
                      />
                    }
                    arrow={true}
                  >
                    <Tag
                      icon={
                        <VibilityWindowIcon
                          style={{ fill: "#ffcc00", width: 14, height: 14 }}
                        />
                      }
                      style={{
                        margin: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gridGap: "3px",
                        cursor: "pointer",
                        height: "20px",
                        fontWeight: "600",
                        padding: "0 4px",
                        minWidth: "20px",
                        backgroundColor: "#EAF6ED",
                        border: "none",
                        color: "#0E8345",
                        width: "fit-content",
                        ...getVibilityWindowStyles(
                          shipment?.schedule_viability_window,
                          shipment?.status,
                          shipment?.scheduled_delivery_date
                        ),
                      }}
                    >
                      {changeToTimeString(
                        shipment?.schedule_viability_window
                      ) || "--hr --min"}
                    </Tag>
                  </Tooltip>
                </Flex>
              </Flex>
            </Flex>
          </div>
        </div>
      )}
      {lightModalOpen &&
        shipment?.shipment_monitor &&
        shipment?.shipment_monitor?.length > 0 && (
          <CustomModalWithoutButton
            modalOpen={lightModalOpen}
            setModalOpen={setLightModalOpen}
            modalTitle="Light"
            modalText=""
            modalSize={615}
          >
            <LightModal
              min={shipment?.product?.temperature_min}
              max={shipment?.product?.temperature_min}
              shipment_monitor={shipment?.shipment_monitor}
            />
          </CustomModalWithoutButton>
        )}
      {humidityModalOpen &&
        shipment?.shipment_monitor &&
        shipment?.shipment_monitor?.length > 0 && (
          <CustomModalWithoutButton
            modalOpen={humidityModalOpen}
            setModalOpen={setHumidityModalOpen}
            modalTitle="Humidity"
            modalText=""
            modalSize={615}
          >
            <HumidityModal
              min={shipment?.product?.temperature_min}
              max={shipment?.product?.temperature_min}
              shipment_monitor={shipment?.shipment_monitor}
            />
          </CustomModalWithoutButton>
        )}
      {tempratureModalOpen &&
        shipment?.shipment_monitor &&
        shipment?.shipment_monitor?.length > 0 && (
          <CustomModalWithoutButton
            modalOpen={tempratureModalOpen}
            setModalOpen={setTempratureModalOpen}
            modalTitle="Temperature"
            modalText=""
            modalSize={615}
          >
            <TemperatureModal
              min={shipment?.product?.temperature_min}
              max={shipment?.product?.temperature_min}
              shipment_monitor={shipment?.shipment_monitor}
            />
          </CustomModalWithoutButton>
        )}

      {batteryModalOpen &&
        shipment?.shipment_monitor &&
        shipment?.shipment_monitor?.length > 0 && (
          <CustomModalWithoutButton
            modalOpen={batteryModalOpen}
            setModalOpen={setBatteryModalOpen}
            modalTitle="Sensor battery life"
            modalText=""
            modalSize={615}
          >
            <BatteryModal shipment_monitor={shipment?.shipment_monitor} />
          </CustomModalWithoutButton>
        )}

      {/* Conditionally render the WeatherLegend component */}
      {showWeatherLayer && weatherLayerType && (
        <WeatherLegend
          activeLayerType={weatherLayerType}
          weatherLayerOptions={weatherLayerOptions}
        />
      )}

      {/* New Bottom Slider Panel - Conditionally rendered */}
      {showWeatherLayer && (
        <div className="bottom-slider-panel">
          <button
            className="play-pause-button"
            aria-label="Play timeline"
            onClick={() => {
              if (isPlaying) {
                setIsPlaying(false);
              } else {
                if (forecastHourOffset >= 72) {
                   setForecastHourOffset(0);
                }
                setIsPlaying(true);
              }
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <div className="slider-and-labels-container">
            <CustomForecastSlider
              value={forecastHourOffset}
              max={72}
              onChange={handleSliderChange}
            />
            <div className="forecast-slider-labels">
               {forecastMarksToShow.map(({ value }) => {
                 return (
                  <div key={value} className="forecast-slider-label">
                    {value === 0 ? 'Now' : dayjs(forecastStartTime).add(value, 'hour').format('ddd HH:mm')}
                  </div>
                 );
               })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeGoolgeMap;
